import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A custom React hook that adds mouse hover events to a DOM element.
 *
 * @template T The type of the DOM element being hovered over.
 * @returns A tuple containing a ref to the DOM element and a boolean indicating whether the element is currently being hovered over.
 */
export const useHover = <T extends HTMLElement>(): [
  React.RefObject<T>,
  boolean,
] => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<T>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, [setIsHovered]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, [setIsHovered]);

  useEffect(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener('mouseenter', handleMouseEnter);
      node.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        node.removeEventListener('mouseenter', handleMouseEnter);
        node.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [handleMouseEnter, handleMouseLeave, ref]);

  return [ref, isHovered];
};

export default useHover;
